import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  ArrowLeft,
  Users,
  Search,
  Download,
  MoreVertical,
  Check,
  X,
  DollarSign,
  QrCode,
  Calendar,
  Mail,
  Ticket,
  Eye,
  FileText,
  User,
  Edit,
  Plus,
  Minus,
  Clock,
  UserPlus,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { goBack } from '../../../utils/navigation';
import BuscadorMatriculados from '../../../components/BuscadorMatriculados';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Función para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export default function AdminEventoInscriptosPage() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [evento, setEvento] = useState(null);
  const [inscripciones, setInscripciones] = useState([]);
  const [campos, setCampos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [inscripcionSeleccionada, setInscripcionSeleccionada] = useState(null);
  const [editandoCantidad, setEditandoCantidad] = useState(null);
  const [nuevaCantidad, setNuevaCantidad] = useState(1);
  const [editandoInscripcion, setEditandoInscripcion] = useState(null);
  const [datosEdicion, setDatosEdicion] = useState({});
  
  // Estados para lista de espera
  const [listaEspera, setListaEspera] = useState([]);
  const [statsListaEspera, setStatsListaEspera] = useState({ pendientes: 0, notificados: 0, inscriptos: 0 });
  const [activeTab, setActiveTab] = useState('inscriptos');
  const [loadingListaEspera, setLoadingListaEspera] = useState(false);

  // Agregar inscripto manualmente
  const [showAddModal, setShowAddModal] = useState(false);
  const [addRespuestas, setAddRespuestas] = useState({});
  const [addEnviarEmail, setAddEnviarEmail] = useState(true);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    fetchData();
  }, [eventoId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/eventos/admin/${eventoId}/inscriptos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvento(res.data.evento);
      
      // Agrupar inscripciones por email para contar entradas por usuario
      // Las canceladas se agrupan separadamente de las activas
      const inscripcionesRaw = res.data.inscripciones;
      const agrupadas = {};
      
      inscripcionesRaw.forEach(insc => {
        const email = insc.email || insc.matricula || insc.id;
        // Usar email + estado como clave para separar activas de canceladas
        const esCancelado = insc.estado === 'cancelado';
        const key = esCancelado ? `${email}_CANCELADO` : email;
        
        if (!agrupadas[key]) {
          agrupadas[key] = {
            ...insc,
            cantidad_entradas: 1,
            inscripciones_ids: [insc.id]
          };
        } else {
          agrupadas[key].cantidad_entradas += 1;
          agrupadas[key].inscripciones_ids.push(insc.id);
          // Actualizar estado de pago si alguna está pagada
          if (insc.payment_status === 'pagado') {
            agrupadas[key].payment_status = 'pagado';
          }
        }
      });
      
      setInscripciones(Object.values(agrupadas));
      setCampos(res.data.campos);
      
      // Cargar lista de espera también
      fetchListaEspera();
    } catch (err) {
      console.error('Error cargando datos:', err);
      alert('Error al cargar inscriptos');
      navigate('/admin/eventos');
    } finally {
      setLoading(false);
    }
  };

  // ==== Agregar inscripto manual ====
  const openAddModal = () => {
    const initial = {};
    (campos || []).forEach((c) => {
      if (c.tipo === 'checkbox') initial[c.id] = false;
      else if (c.tipo === 'multiselect') initial[c.id] = [];
      else initial[c.id] = '';
    });
    setAddRespuestas(initial);
    setAddEnviarEmail(true);
    setAddError('');
    setShowAddModal(true);
  };

  const setAddCampo = (campoId, valor) => {
    setAddRespuestas((p) => ({ ...p, [campoId]: valor }));
  };

  const handlePickMatriculado = (m) => {
    const mapping = {
      nombre: m.nombre,
      apellido: m.apellido,
      dni: m.dni,
      email: m.email,
      telefono: m.telefono,
      matricula: m.matricula,
      distrito: m.distrito,
    };
    const next = { ...addRespuestas };
    (campos || []).forEach((c) => {
      const key = c.prellenar_con;
      if (key && mapping[key]) {
        next[c.id] = mapping[key];
      } else if (!next[c.id]) {
        // best-effort por tipo email
        const labelLower = (c.label || '').toLowerCase();
        if ((c.tipo === 'email' || labelLower.includes('email') || labelLower.includes('correo')) && m.email) {
          next[c.id] = m.email;
        }
      }
    });
    setAddRespuestas(next);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/eventos/admin/${eventoId}/inscriptos`,
        { respuestas: addRespuestas, enviar_email: addEnviarEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAddModal(false);
      await fetchData();
    } catch (err) {
      setAddError(err.response?.data?.detail || 'No se pudo crear la inscripción.');
    } finally {
      setAddSaving(false);
    }
  };

  const fetchListaEspera = async () => {
    setLoadingListaEspera(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/eventos/admin/${eventoId}/lista-espera`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListaEspera(res.data.lista || []);
      setStatsListaEspera({
        pendientes: res.data.pendientes || 0,
        notificados: res.data.notificados || 0,
        inscriptos: res.data.inscriptos || 0
      });
    } catch (err) {
      console.error('Error cargando lista de espera:', err);
      setListaEspera([]);
    } finally {
      setLoadingListaEspera(false);
    }
  };

  const handleMoverAInscripto = async (listaEsperaId) => {
    if (!window.confirm('¿Mover esta persona a inscripto? Se creará una inscripción con los datos que completó.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/eventos/admin/${eventoId}/lista-espera/${listaEsperaId}/mover-a-inscripto`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Inscripción creada exitosamente');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear inscripción');
    }
  };

  const handleEliminarDeListaEspera = async (listaEsperaId) => {
    if (!window.confirm('¿Eliminar de la lista de espera?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/eventos/admin/${eventoId}/lista-espera/${listaEsperaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchListaEspera();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleMarcarPago = async (inscripcionId) => {
    const metodo = window.prompt('Método de pago (transferencia, efectivo, etc.):', 'transferencia');
    if (!metodo) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/eventos/admin/inscripciones/${inscripcionId}/pago`, 
        { metodo_pago: metodo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al marcar pago');
    }
  };

  const handleCancelar = async (inscripcionId) => {
    if (!window.confirm('¿Cancelar esta inscripción?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/eventos/admin/inscripciones/${inscripcionId}/cancelar`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleReactivar = async (inscripcionId) => {
    if (!window.confirm('¿Reactivar esta inscripción?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/eventos/admin/inscripciones/${inscripcionId}/reactivar`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Inscripción reactivada exitosamente');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al reactivar');
    }
  };

  const handleConfirmar = async (inscripcionId) => {
    if (!window.confirm('¿Confirmar esta inscripción manualmente? Pasará a estado "confirmado".')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${API_URL}/eventos/admin/inscripciones/${inscripcionId}/confirmar`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message || 'Inscripción confirmada');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al confirmar');
    }
  };

  const handleModificarCantidad = async (email, cantidad) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API_URL}/eventos/admin/inscripciones/modificar-cantidad`, 
        { email, nueva_cantidad: cantidad, evento_id: eventoId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      setEditandoCantidad(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al modificar cantidad');
    }
  };

  const abrirEditorCantidad = (inscripcion) => {
    setEditandoCantidad(inscripcion);
    setNuevaCantidad(inscripcion.cantidad_entradas || 1);
  };

  // Abrir editor de datos de inscripción
  const abrirEditorInscripcion = (inscripcion) => {
    setEditandoInscripcion(inscripcion);
    setDatosEdicion({
      email: inscripcion.email || '',
      respuestas: { ...inscripcion.respuestas } || {},
      notas_admin: inscripcion.notas_admin || ''
    });
  };

  // Guardar cambios de edición
  const handleGuardarEdicion = async () => {
    if (!editandoInscripcion) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/eventos/admin/inscripciones/${editandoInscripcion.id}/editar`,
        {
          respuestas: datosEdicion.respuestas,
          email: datosEdicion.email !== editandoInscripcion.email ? datosEdicion.email : undefined,
          notas_admin: datosEdicion.notas_admin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Inscripción actualizada correctamente');
      setEditandoInscripcion(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar cambios');
    }
  };

  // Actualizar campo del formulario en edición
  const handleCampoEdicion = (campoId, valor) => {
    setDatosEdicion(prev => ({
      ...prev,
      respuestas: {
        ...prev.respuestas,
        [campoId]: valor
      }
    }));
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/eventos/admin/${eventoId}/inscriptos/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Descargar CSV
      const blob = new Blob([res.data.content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = res.data.filename;
      link.click();
    } catch (err) {
      alert('Error al exportar');
    }
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      confirmado: 'bg-green-100 text-green-700',
      pendiente_pago: 'bg-amber-100 text-amber-700',
      cancelado: 'bg-red-100 text-red-700'
    };
    return <Badge className={estilos[estado] || 'bg-gray-100 text-gray-700'}>{estado}</Badge>;
  };

  const getPagoBadge = (status) => {
    const estilos = {
      pagado: 'bg-green-100 text-green-700',
      pendiente: 'bg-amber-100 text-amber-700',
      no_requerido: 'bg-gray-100 text-gray-500'
    };
    const labels = {
      pagado: 'Pagado',
      pendiente: 'Pendiente',
      no_requerido: 'N/A'
    };
    return <Badge className={estilos[status] || estilos.no_requerido}>{labels[status] || status}</Badge>;
  };

  // Obtener valor de respuesta para mostrar
  const getRespuesta = (inscripcion, campoId) => {
    const valor = inscripcion.respuestas?.[campoId];
    if (valor === undefined || valor === null || valor === '') return '-';
    if (Array.isArray(valor)) return valor.join(', ');
    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    return String(valor);
  };

  // Filtrar inscripciones
  const inscripcionesFiltradas = inscripciones.filter(insc => {
    // Filtro de búsqueda
    const searchLower = searchTerm.toLowerCase();
    const matchSearch = !searchTerm || 
      insc.email?.toLowerCase().includes(searchLower) ||
      insc.matricula?.toLowerCase().includes(searchLower) ||
      Object.values(insc.respuestas || {}).some(v => 
        String(v).toLowerCase().includes(searchLower)
      );
    
    // Filtro de estado
    const matchEstado = filtroEstado === 'todos' || 
      (filtroEstado === 'pagados' && insc.payment_status === 'pagado') ||
      (filtroEstado === 'pendientes' && insc.payment_status === 'pendiente') ||
      (filtroEstado === 'cancelados' && insc.estado === 'cancelado');
    
    return matchSearch && matchEstado;
  });

  // Estadísticas - considerando entradas totales
  const totalEntradas = inscripciones.reduce((sum, i) => sum + (i.cantidad_entradas || 1), 0);
  const stats = {
    total: inscripciones.length,  // Personas únicas
    totalEntradas: totalEntradas,  // Total de entradas
    activos: inscripciones.filter(i => i.estado !== 'cancelado').length,
    pagados: inscripciones.filter(i => i.payment_status === 'pagado').length,
    checkedIn: inscripciones.filter(i => i.checked_in).length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  // Determinar qué columnas mostrar (máx 4 campos del formulario)
  const columnasFormulario = campos.slice(0, 4);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => goBack()}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{evento?.nombre}</h1>
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(evento?.fecha_evento)} {evento?.hora_evento && `- ${evento?.hora_evento}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={openAddModal} data-testid="abrir-agregar-inscripto">
            <UserPlus className="w-4 h-4 mr-2" />
            Agregar inscripto
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className={`grid grid-cols-2 ${evento?.multiple_entradas ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 mb-6`}>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activos}</p>
                <p className="text-xs text-gray-500">Inscriptos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {evento?.multiple_entradas && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Ticket className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalEntradas}</p>
                  <p className="text-xs text-gray-500">Entradas totales</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pagados}</p>
                <p className="text-xs text-gray-500">Pagados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <QrCode className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.checkedIn}</p>
                <p className="text-xs text-gray-500">Check-in</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <X className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total - stats.activos}</p>
                <p className="text-xs text-gray-500">Cancelados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {statsListaEspera.pendientes > 0 && (
          <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setActiveTab('lista-espera')}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsListaEspera.pendientes}</p>
                  <p className="text-xs text-gray-500">En espera</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs: Inscriptos / Lista de Espera */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="inscriptos" className="gap-2">
            <Users className="w-4 h-4" />
            Inscriptos ({stats.activos})
          </TabsTrigger>
          <TabsTrigger value="lista-espera" className="gap-2">
            <Clock className="w-4 h-4" />
            Lista de Espera ({statsListaEspera.pendientes})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inscriptos">
          {/* Filtros */}
          <Card className="mb-6">
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por email, matrícula o respuestas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  {['todos', 'pagados', 'pendientes', 'cancelados'].map(filtro => (
                    <Button
                      key={filtro}
                      variant={filtroEstado === filtro ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFiltroEstado(filtro)}
                      className={filtroEstado === filtro ? 'bg-violet-600' : ''}
                    >
                      {filtro.charAt(0).toUpperCase() + filtro.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">#</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Matrícula</TableHead>
                  {evento?.multiple_entradas && (
                    <TableHead className="text-center">Entradas</TableHead>
                  )}
                  {columnasFormulario.map(campo => (
                    <TableHead key={campo.id}>{campo.label}</TableHead>
                  ))}
                  <TableHead>Estado</TableHead>
                  <TableHead>Pago</TableHead>
                  {evento?.modalidad_admision === 'ticket_qr' && (
                    <TableHead>Check-in</TableHead>
                  )}
                  <TableHead className="w-[120px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inscripcionesFiltradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8 + columnasFormulario.length} className="text-center py-8 text-gray-500">
                      No hay inscriptos que mostrar
                    </TableCell>
                  </TableRow>
                ) : (
                  inscripcionesFiltradas.map((insc, index) => (
                    <TableRow key={insc.id} className={insc.estado === 'cancelado' ? 'opacity-50' : ''}>
                      <TableCell className="text-gray-500">{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {insc.email}
                        </div>
                      </TableCell>
                      <TableCell>{insc.matricula || '-'}</TableCell>
                      {evento?.multiple_entradas && (
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Badge variant="outline" className="font-semibold">
                              {insc.cantidad_entradas || 1}
                            </Badge>
                            {insc.estado !== 'cancelado' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => abrirEditorCantidad(insc)}
                                title="Editar cantidad de entradas"
                                data-testid={`btn-editar-cantidad-${index}`}
                              >
                                <Edit className="w-3 h-3 text-gray-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {columnasFormulario.map(campo => (
                        <TableCell key={campo.id} className="max-w-[200px] truncate">
                          {getRespuesta(insc, campo.id)}
                        </TableCell>
                      ))}
                      <TableCell>{getEstadoBadge(insc.estado)}</TableCell>
                      <TableCell>
                        {getPagoBadge(insc.payment_status)}
                        {insc.monto_a_pagar > 0 && insc.payment_status === 'pendiente' && (
                          <span className="text-xs text-gray-500 block">
                            ${insc.monto_a_pagar?.toLocaleString()}
                          </span>
                        )}
                      </TableCell>
                      {evento?.modalidad_admision === 'ticket_qr' && (
                        <TableCell>
                          {insc.checked_in ? (
                            <Badge className="bg-green-100 text-green-700">
                              <Check className="w-3 h-3 mr-1" />
                              Sí
                            </Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setInscripcionSeleccionada(insc)}
                            title="Ver formulario completo"
                            data-testid={`btn-ver-formulario-${index}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setInscripcionSeleccionada(insc)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver formulario completo
                            </DropdownMenuItem>
                            {insc.estado !== 'cancelado' && (
                              <DropdownMenuItem onClick={() => abrirEditorInscripcion(insc)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar datos
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {insc.payment_status === 'pendiente' && (
                              <DropdownMenuItem onClick={() => handleMarcarPago(insc.id)}>
                                <DollarSign className="w-4 h-4 mr-2" />
                                Marcar pago recibido
                              </DropdownMenuItem>
                            )}
                            {insc.ticket_id && (
                              <DropdownMenuItem onClick={() => window.open(`/eventos/verificar-ticket/${insc.ticket_id}`, '_blank')}>
                                <QrCode className="w-4 h-4 mr-2" />
                                Ver ticket
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {insc.estado !== 'cancelado' && insc.estado !== 'confirmado' && (
                              <DropdownMenuItem
                                onClick={() => handleConfirmar(insc.id)}
                                className="text-green-700"
                                data-testid={`menu-confirmar-${insc.id}`}
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Confirmar inscripción
                              </DropdownMenuItem>
                            )}
                            {insc.estado !== 'cancelado' && (
                              <DropdownMenuItem 
                                onClick={() => handleCancelar(insc.id)}
                                className="text-red-600"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Cancelar inscripción
                              </DropdownMenuItem>
                            )}
                            {insc.estado === 'cancelado' && (
                              <DropdownMenuItem 
                                onClick={() => handleReactivar(insc.id)}
                                className="text-green-600"
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Reactivar inscripción
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      </TabsContent>

      {/* Tab: Lista de Espera */}
      <TabsContent value="lista-espera">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-orange-600" />
              Lista de Espera
              {statsListaEspera.pendientes > 0 && (
                <Badge variant="outline" className="ml-2">
                  {statsListaEspera.pendientes} esperando
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingListaEspera ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
              </div>
            ) : listaEspera.filter(l => l.estado === 'pendiente' || l.estado === 'notificado').length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No hay personas en lista de espera</p>
                <p className="text-sm mt-1">
                  Cuando alguien intente inscribirse con el cupo lleno o inscripciones cerradas,
                  aparecerá aquí.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Posición</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Datos</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="w-[150px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listaEspera
                      .filter(l => l.estado === 'pendiente' || l.estado === 'notificado')
                      .map((registro, idx) => {
                        // Obtener nombre de las respuestas
                        const nombre = Object.entries(registro.respuestas || {})
                          .find(([key]) => key.toLowerCase().includes('nombre'))?.[1] || '';
                        const apellido = Object.entries(registro.respuestas || {})
                          .find(([key]) => key.toLowerCase().includes('apellido'))?.[1] || '';
                        
                        return (
                          <TableRow key={registro.id}>
                            <TableCell>
                              <Badge variant="outline" className="font-bold">
                                #{registro.posicion || idx + 1}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                {registro.email}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {nombre || apellido ? (
                                  <span className="font-medium">{nombre} {apellido}</span>
                                ) : (
                                  <span className="text-gray-400">Sin nombre</span>
                                )}
                                {registro.cantidad_entradas > 1 && (
                                  <Badge variant="outline" className="ml-2">
                                    {registro.cantidad_entradas} entradas
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {registro.estado === 'pendiente' && (
                                <Badge className="bg-orange-500">En espera</Badge>
                              )}
                              {registro.estado === 'notificado' && (
                                <Badge className="bg-blue-500">Notificado</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {registro.created_at 
                                ? new Date(registro.created_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleMoverAInscripto(registro.id)}
                                  title="Mover a inscripto"
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleEliminarDeListaEspera(registro.id)}
                                  title="Eliminar de lista"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Historial de movidos a inscripto */}
            {listaEspera.filter(l => l.estado === 'inscripto').length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Historial: Movidos a inscripto ({listaEspera.filter(l => l.estado === 'inscripto').length})
                </h4>
                <div className="space-y-2">
                  {listaEspera.filter(l => l.estado === 'inscripto').map(registro => (
                    <div key={registro.id} className="flex items-center justify-between text-sm bg-gray-50 rounded p-2">
                      <span>{registro.email}</span>
                      <span className="text-gray-500">
                        {registro.inscripto_at 
                          ? new Date(registro.inscripto_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })
                          : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>

      {/* Modal: Detalle del Formulario del Inscripto */}
      <Dialog open={!!inscripcionSeleccionada} onOpenChange={() => setInscripcionSeleccionada(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-600" />
              Formulario de Inscripción
            </DialogTitle>
          </DialogHeader>
          
          {inscripcionSeleccionada && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-6">
                {/* Info básica del inscripto */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Información del Inscripto
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium">{inscripcionSeleccionada.email || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Matrícula:</span>
                      <p className="font-medium">{inscripcionSeleccionada.matricula || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Estado:</span>
                      <div className="mt-1">{getEstadoBadge(inscripcionSeleccionada.estado)}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Pago:</span>
                      <div className="mt-1">{getPagoBadge(inscripcionSeleccionada.payment_status)}</div>
                    </div>
                    {evento?.multiple_entradas && inscripcionSeleccionada.cantidad_entradas > 1 && (
                      <div>
                        <span className="text-gray-500">Entradas:</span>
                        <p className="font-medium">{inscripcionSeleccionada.cantidad_entradas}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Fecha inscripción:</span>
                      <p className="font-medium">
                        {inscripcionSeleccionada.created_at 
                          ? new Date(inscripcionSeleccionada.created_at).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Respuestas del formulario */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2 border-b pb-2">
                    <FileText className="w-4 h-4" />
                    Respuestas del Formulario
                  </h3>
                  
                  {campos.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">
                      Este evento no tiene campos de formulario personalizados.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {campos.map((campo) => {
                        const valor = inscripcionSeleccionada.respuestas?.[campo.id];
                        let valorMostrar = '-';
                        
                        if (valor !== undefined && valor !== null && valor !== '') {
                          if (Array.isArray(valor)) {
                            valorMostrar = valor.join(', ');
                          } else if (typeof valor === 'boolean') {
                            valorMostrar = valor ? 'Sí' : 'No';
                          } else {
                            valorMostrar = String(valor);
                          }
                        }
                        
                        return (
                          <div key={campo.id} className="border-b border-gray-100 pb-3 last:border-0">
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  {campo.label}
                                  {campo.required && <span className="text-red-500">*</span>}
                                </p>
                                <p className="font-medium mt-1 whitespace-pre-wrap">
                                  {valorMostrar}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {campo.type}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Acciones rápidas */}
                <div className="flex gap-2 pt-4 border-t">
                  {inscripcionSeleccionada.payment_status === 'pendiente' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        handleMarcarPago(inscripcionSeleccionada.id);
                        setInscripcionSeleccionada(null);
                      }}
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      Marcar pago
                    </Button>
                  )}
                  {inscripcionSeleccionada.ticket_id && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(`/eventos/verificar-ticket/${inscripcionSeleccionada.ticket_id}`, '_blank')}
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      Ver ticket
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="ml-auto"
                    onClick={() => setInscripcionSeleccionada(null)}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Editar cantidad de entradas */}
      <Dialog open={!!editandoCantidad} onOpenChange={(open) => !open && setEditandoCantidad(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5" />
              Modificar cantidad de entradas
            </DialogTitle>
          </DialogHeader>
          {editandoCantidad && (
            <div className="space-y-4 py-4">
              <div className="text-sm text-gray-600">
                <p><strong>Email:</strong> {editandoCantidad.email}</p>
                <p><strong>Cantidad actual:</strong> {editandoCantidad.cantidad_entradas || 1} entrada(s)</p>
              </div>
              
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNuevaCantidad(Math.max(1, nuevaCantidad - 1))}
                  disabled={nuevaCantidad <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <div className="text-3xl font-bold w-16 text-center">
                  {nuevaCantidad}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNuevaCantidad(Math.min(evento?.max_entradas_por_usuario || 10, nuevaCantidad + 1))}
                  disabled={nuevaCantidad >= (evento?.max_entradas_por_usuario || 10)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Máximo: {evento?.max_entradas_por_usuario || 10} entradas por usuario
              </p>
              
              {nuevaCantidad !== (editandoCantidad.cantidad_entradas || 1) && (
                <div className={`text-sm text-center p-2 rounded ${
                  nuevaCantidad > (editandoCantidad.cantidad_entradas || 1) 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-amber-50 text-amber-700'
                }`}>
                  {nuevaCantidad > (editandoCantidad.cantidad_entradas || 1) 
                    ? `Se agregarán ${nuevaCantidad - (editandoCantidad.cantidad_entradas || 1)} entrada(s)`
                    : `Se cancelarán ${(editandoCantidad.cantidad_entradas || 1) - nuevaCantidad} entrada(s)`
                  }
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditandoCantidad(null)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={() => handleModificarCantidad(editandoCantidad.email, nuevaCantidad)}
                  disabled={nuevaCantidad === (editandoCantidad.cantidad_entradas || 1)}
                >
                  Guardar cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Editar datos de inscripción */}
      <Dialog open={!!editandoInscripcion} onOpenChange={(open) => !open && setEditandoInscripcion(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-violet-600" />
              Editar Inscripción
            </DialogTitle>
          </DialogHeader>
          
          {editandoInscripcion && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-6">
                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <Input
                    type="email"
                    value={datosEdicion.email || ''}
                    onChange={(e) => setDatosEdicion(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email del inscripto"
                  />
                </div>

                {/* Campos del formulario */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 border-b pb-2">Datos del Formulario</h3>
                  {campos.map(campo => (
                    <div key={campo.id} className="space-y-1">
                      <label className="text-sm font-medium text-gray-700">
                        {campo.label}
                        {campo.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      
                      {campo.tipo === 'textarea' ? (
                        <textarea
                          className="w-full p-2 border rounded-md text-sm min-h-[80px]"
                          value={datosEdicion.respuestas?.[campo.id] || ''}
                          onChange={(e) => handleCampoEdicion(campo.id, e.target.value)}
                          placeholder={campo.placeholder}
                        />
                      ) : campo.tipo === 'select' || campo.tipo === 'dropdown' ? (
                        <select
                          className="w-full p-2 border rounded-md text-sm"
                          value={datosEdicion.respuestas?.[campo.id] || ''}
                          onChange={(e) => handleCampoEdicion(campo.id, e.target.value)}
                        >
                          <option value="">Seleccionar...</option>
                          {campo.options?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : campo.tipo === 'checkbox' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={datosEdicion.respuestas?.[campo.id] === true || datosEdicion.respuestas?.[campo.id] === 'true'}
                            onChange={(e) => handleCampoEdicion(campo.id, e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-gray-600">{campo.placeholder || 'Sí'}</span>
                        </div>
                      ) : campo.tipo === 'multiselect' ? (
                        <div className="space-y-1 border rounded-md p-2 max-h-[150px] overflow-y-auto">
                          {campo.options?.map(opt => (
                            <label key={opt} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={(datosEdicion.respuestas?.[campo.id] || []).includes(opt)}
                                onChange={(e) => {
                                  const current = datosEdicion.respuestas?.[campo.id] || [];
                                  const newValue = e.target.checked 
                                    ? [...current, opt]
                                    : current.filter(v => v !== opt);
                                  handleCampoEdicion(campo.id, newValue);
                                }}
                                className="w-4 h-4"
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <Input
                          type={campo.tipo === 'number' ? 'number' : campo.tipo === 'date' ? 'date' : 'text'}
                          value={datosEdicion.respuestas?.[campo.id] || ''}
                          onChange={(e) => handleCampoEdicion(campo.id, e.target.value)}
                          placeholder={campo.placeholder}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Notas del admin */}
                <div className="space-y-2 border-t pt-4">
                  <label className="text-sm font-medium text-gray-700">Notas internas (solo admin)</label>
                  <textarea
                    className="w-full p-2 border rounded-md text-sm min-h-[60px]"
                    value={datosEdicion.notas_admin || ''}
                    onChange={(e) => setDatosEdicion(prev => ({ ...prev, notas_admin: e.target.value }))}
                    placeholder="Notas o comentarios sobre esta inscripción..."
                  />
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setEditandoInscripcion(null)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleGuardarEdicion}>
                    Guardar cambios
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Agregar inscripto manualmente */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-violet-600" />
              Agregar inscripto a "{evento?.nombre}"
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitAdd} className="space-y-4">
            <div className="bg-violet-50 border border-violet-200 rounded-md p-3 space-y-2">
              <p className="text-xs text-violet-700">
                Buscá un matriculado con cuenta para autocompletar los campos del formulario.
              </p>
              <BuscadorMatriculados onSelect={handlePickMatriculado} />
            </div>

            {campos.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                Este evento no tiene campos personalizados. La persona quedará inscripta solo con los datos de identidad si los completaste arriba.
              </p>
            ) : (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {campos.map((c) => (
                  <CampoFormularioRender
                    key={c.id}
                    campo={c}
                    valor={addRespuestas[c.id]}
                    onChange={(v) => setAddCampo(c.id, v)}
                  />
                ))}
              </div>
            )}

            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Enviar email de confirmación</p>
                <p className="text-xs text-gray-500">Si lo desactivás, no se le notifica al inscripto.</p>
              </div>
              <Switch
                checked={addEnviarEmail}
                onCheckedChange={setAddEnviarEmail}
                data-testid="toggle-enviar-email-inscripto"
              />
            </div>

            {addError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
                {addError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} disabled={addSaving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={addSaving} className="bg-violet-600 hover:bg-violet-700" data-testid="submit-agregar-inscripto">
                {addSaving ? 'Agregando…' : 'Agregar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampoFormularioRender({ campo, valor, onChange }) {
  const label = (
    <Label className="text-sm">
      {campo.label}
      {campo.required && <span className="text-red-500 ml-1">*</span>}
    </Label>
  );
  const placeholder = campo.placeholder || '';

  if (campo.tipo === 'textarea') {
    return (
      <div className="space-y-1">
        {label}
        <Textarea value={valor || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={campo.required} />
      </div>
    );
  }
  if (campo.tipo === 'select' || campo.tipo === 'dropdown') {
    return (
      <div className="space-y-1">
        {label}
        <Select value={valor || ''} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder={placeholder || 'Seleccionar…'} /></SelectTrigger>
          <SelectContent>
            {(campo.options || []).map((opt, idx) => (
              <SelectItem key={`${opt}-${idx}`} value={String(opt)}>{String(opt)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (campo.tipo === 'multiselect') {
    const seleccionados = Array.isArray(valor) ? valor : [];
    const toggle = (opt) => {
      onChange(seleccionados.includes(opt) ? seleccionados.filter((x) => x !== opt) : [...seleccionados, opt]);
    };
    return (
      <div className="space-y-1">
        {label}
        <div className="flex flex-wrap gap-2">
          {(campo.options || []).map((opt, idx) => (
            <label key={`${opt}-${idx}`} className="flex items-center gap-2 text-sm border rounded-md px-2 py-1 cursor-pointer">
              <Checkbox checked={seleccionados.includes(String(opt))} onCheckedChange={() => toggle(String(opt))} />
              {String(opt)}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (campo.tipo === 'checkbox') {
    return (
      <div className="flex items-center gap-2">
        <Checkbox checked={!!valor} onCheckedChange={(v) => onChange(!!v)} />
        {label}
      </div>
    );
  }
  const type = campo.tipo === 'email' ? 'email'
    : campo.tipo === 'number' ? 'number'
    : campo.tipo === 'phone' ? 'tel'
    : campo.tipo === 'date' ? 'date'
    : 'text';
  return (
    <div className="space-y-1">
      {label}
      <Input
        type={type}
        value={valor || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={campo.required}
      />
    </div>
  );
}
